import {
  DialogActions,
  DialogContent,
  Grid,
  Button,
  Typography,
  MenuItem,
  Box,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import LabelTextComponent from "app/components/common/LabelTextComponent";
import { CustomTextField, CustomSelectBox } from "assets/styles/globalStyledComponent";
import React, { useEffect, useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { getWhatsAppTemplates } from "app/services/schoolService/com/wa.service";
import { listWhatsAppVariables } from "app/services/schoolReportingServices/communicationSetting.service";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";
import { ValidateFileSize } from "app/utils/helper";
import TableActionButtons from "app/components/common/tableComponent/TableActionButtons";
import DraggableList from "app/components/common/DraggableList/DraggableList";

const EditTemplateDialog = ({
  open = false,
  onClose = () => {},
  editData = {},
  onUpdate = () => {}, 
  loading = false,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variablePriorities, setVariablePriorities] = useState({});
  const [variableNames, setVariableNames] = useState({});
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  const imageInputRef = useRef(null);
  const { handleUploadImage } = useFolderWiseFileUploader();

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const { valid, message } = ValidateFileSize(file, {
      validateImage: true,
      imageLimit: 0.5 * 1048576, // 0.5MB
    });

    if (!valid) {
      toast.error(message);
      return;
    }

    setUploadingImage(true);
    try {
      const result = await handleUploadImage(
        false,
        e,
        "withSession",
        "COMMUNICATION",
        "WHATSAPP_TEMPLATES"
      );
      setImageUrl(result.fileURL);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle image delete
  const handleDeleteImage = () => {
    setImageUrl("");
  };

  // Trigger file input click
  const handleEditImage = () => {
    imageInputRef.current?.click();
  };

  // Fetch WhatsApp templates from API
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ["getWhatsAppTemplates"],
    queryFn: () => getWhatsAppTemplates(),
    enabled: open,
    select: (data) => {
      const templates = data?.data?.list || [];
      // Filter only APPROVED templates
      return templates.filter((template) => template.status === "APPROVED");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to fetch templates"
      );
    },
  });

  // Fetch WhatsApp variables based on purpose
  const { data: variablesData } = useQuery({
    queryKey: ["listWhatsAppVariables", editData?.purpose],
    queryFn: () => listWhatsAppVariables(editData?.purpose),
    enabled: open && !!editData?.purpose,
    select: (data) => {
      const variables = data?.data?.list || [];
      // Ensure it's always an array
      return Array.isArray(variables) ? variables : [];
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to fetch variables"
      );
    },
  });

  // Initialize form when editData changes
  useEffect(() => {
    if (editData && open && templatesData) {
      // Find the matching template if editData has a template name
      if (editData.name) {
        const matchedTemplate = templatesData.find(
          (t) => t.name === editData.name
        );
        setSelectedTemplate(matchedTemplate || null);
      }

      // Initialize variable priorities from editData
      if (editData.parameters && Array.isArray(editData.parameters)) {
        const priorities = {};
        const names = {};
        editData.parameters.forEach((param, index) => {
          // Store both priority and variable name
          priorities[`{{${index + 1}}}`] = index + 1;
          names[`{{${index + 1}}}`] = param;
        });
        setVariablePriorities(priorities);
        setVariableNames(names);
      }

      // Initialize image URL from editData
      if (editData.link) {
        setImageUrl(editData.link);
      }
    }
  }, [editData, templatesData, open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setVariablePriorities({});
      setVariableNames({});
      setImageUrl("");
    }
  }, [open]);

  // Handle template selection
  const handleTemplateChange = (event) => {
    const templateName = event.target.value;
    const template = templatesData.find((t) => t.name === templateName);
    setSelectedTemplate(template);

    // Reset priorities when template changes
    setVariablePriorities({});
    setVariableNames({});
    setImageUrl("");
  };

  // Handle variable selection
  const handleVariableSelect = (placeholder, variableKey) => {
    setVariableNames((prev) => {
      const updated = {
        ...prev,
        [placeholder]: variableKey,
      };
      return updated;
    });
    
    // Auto-assign priority if not already set
    if (!variablePriorities[placeholder]) {
      setVariablePriorities((prev) => {
        const updated = {
          ...prev,
          [placeholder]: Object.keys(prev).length + 1,
        };
        return updated;
      });
    }
  };

  // Handle removing a variable
  const handleRemoveVariable = (placeholder) => {
    const removedPriority = variablePriorities[placeholder];
    
    // Remove the variable
    setVariableNames((prev) => {
      const newNames = { ...prev };
      delete newNames[placeholder];
      return newNames;
    });
    
    // Adjust priorities
    setVariablePriorities((prev) => {
      const newPriorities = { ...prev };
      delete newPriorities[placeholder];
      
      // Shift down priorities that were higher than the removed one
      Object.keys(newPriorities).forEach((key) => {
        if (newPriorities[key] > removedPriority) {
          newPriorities[key] -= 1;
        }
      });
      
      return newPriorities;
    });
  };

  // Get selected variable count
  const getSelectedCount = () => {
    return Object.keys(variableNames).filter(key => variableNames[key]).length;
  };

  // Get available variables (not yet selected)
  const getAvailableVariables = () => {
    const selectedKeys = Object.values(variableNames);
    return (variablesData || []).filter(v => !selectedKeys.includes(v.key));
  };

  // Transform selected variables to DraggableList format
  const getSelectedVariablesForDraggable = () => {
    return templateVariables
      .filter((placeholder) => variableNames[placeholder])
      .sort((a, b) => variablePriorities[a] - variablePriorities[b])
      .map((placeholder) => {
        const variable = (variablesData || []).find(
          (v) => v.key === variableNames[placeholder]
        );
        return {
          id: placeholder,
          title: variable?.key || variableNames[placeholder],
          subtitle: variable?.label || "",
        };
      });
  };

  // Handle drag-and-drop reorder
  const handleVariableReorder = (reorderedItems) => {
    const newPriorities = {};
    reorderedItems.forEach((item, index) => {
      newPriorities[item.id] = index + 1;
    });
    setVariablePriorities(newPriorities);
  };

  // Handle remove from DraggableList
  const handleDraggableRemove = (itemId) => {
    handleRemoveVariable(itemId);
  };

  // Extract template text
  const getTemplateText = () => {
    if (!selectedTemplate) return "";
    return selectedTemplate.bodyText || selectedTemplate.sampleBodyText || "";
  };

  // Get template variables as placeholders
  const getTemplateVariables = () => {
    if (!selectedTemplate || !selectedTemplate.num_of_parameters) return [];
    
    const variables = [];
    for (let i = 0; i < selectedTemplate.num_of_parameters; i++) {
      variables.push(`{{${i + 1}}}`);
    }
    return variables;
  };

  const templateVariables = getTemplateVariables();

  // Handle save
  const handleSaveClick = () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }

    // Validate image URL for IMAGE type templates
    if (selectedTemplate.type === "IMAGE" && !imageUrl.trim()) {
      toast.error("Please upload an image for IMAGE type template");
      return;
    }
    if (selectedTemplate.type === "IMAGE") {
    }

    // Validation 3: Check if template has parameters
    if (templateVariables.length === 0) {
    } else {
      // Validate all variables have priorities
      const missingPriorities = templateVariables.filter(
        (variable) => !variablePriorities[variable]
      );
      
      if (missingPriorities.length > 0) {
        toast.error(`Please select all ${templateVariables.length} variables`);
        return;
      }

      // Validate all variables have names
      const missingNames = templateVariables.filter(
        (variable) => !variableNames[variable] || !variableNames[variable].trim()
      );
      
      if (missingNames.length > 0) {
        toast.error(`Please provide names for all ${templateVariables.length} variables`);
        return;
      }
    }

    // Sort variables by priority and extract their names
    const sortedVariables = [...templateVariables].sort(
      (a, b) => variablePriorities[a] - variablePriorities[b]
    );
    
    const parametersArray = sortedVariables.map((placeholder) => variableNames[placeholder]);

    const payload = {
      purpose: editData?.purpose,
      name: selectedTemplate.name,
      type: selectedTemplate.type,
      parameters: parametersArray,
      num_of_parameters: selectedTemplate.num_of_parameters,
      template: getTemplateText(),
    };

    // Add link only if it's an IMAGE type template
    if (selectedTemplate.type === "IMAGE" && imageUrl.trim()) {
      payload.link = imageUrl.trim();
    }

    onUpdate(payload);
  };

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title="Map Template With Variables"
      minWidth="1200px"
      maxWidth="1400px"
    >
      <DialogContent sx={{ mt: 2, p: 0 }}>
        <Grid container sx={{ height: "calc(100vh - 200px)", minHeight: "600px" }}>
          {/* Left Panel - Controls */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            sx={{ 
              borderRight: "2px solid #e0e0e0",
              p: 3,
              overflowY: "auto",
              backgroundColor: "#fafafa"
            }}
          >
            <Stack spacing={3}>
              {/* Purpose and Template Name in one row */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4.8}>
                  <TextFieldLabel title="Purpose" required />
                  <CustomTextField
                    fullWidth
                    size="small"
                    value={editData?.purpose || ""}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={7.2}>
                  <TextFieldLabel title="Template Name" required />
                  <CustomSelectBox
                    fullWidth
                    value={selectedTemplate?.name || ""}
                    onChange={handleTemplateChange}
                    displayEmpty
                    disabled={templatesLoading}
                  >
                    <MenuItem value="" disabled>
                      {templatesLoading ? "Loading..." : "Select Template"}
                    </MenuItem>
                    {templatesData?.map((template) => (
                      <MenuItem key={template.id} value={template.name}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                </Grid>
              </Grid>

              {selectedTemplate && (
                <>
                  {/* Template Info */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <LabelTextComponent
                        label="Type"
                        text={selectedTemplate.type}
                        backgroundColor
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <LabelTextComponent
                        label="Category"
                        text={selectedTemplate.category}
                        backgroundColor
                      />
                    </Grid>
                  </Grid>

                  {/* Variables Mapping */}
                  {templateVariables.length > 0 && (
                    <Box>
                      <TextFieldLabel title="Set Variables" required />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", mb: 2, display: "block" }}
                      >
                        Select {selectedTemplate.num_of_parameters} variable(s). 
                        Selected: {getSelectedCount()}/{selectedTemplate.num_of_parameters}
                      </Typography>

                      {/* Dropdown to add new variable */}
                      <CustomSelectBox
                        fullWidth
                        sx={{ mb: 2 }}
                        value=""
                        onChange={(e) => {
                          const nextPlaceholder = templateVariables.find(
                            (p) => !variableNames[p]
                          );
                          if (nextPlaceholder) {
                            handleVariableSelect(nextPlaceholder, e.target.value);
                          }
                        }}
                        displayEmpty
                        disabled={
                          getSelectedCount() >=
                          selectedTemplate.num_of_parameters
                        }
                      >
                        <MenuItem value="" disabled>
                          {getSelectedCount() >=
                          selectedTemplate.num_of_parameters
                            ? "All variables selected"
                            : "Select a variable to add"}
                        </MenuItem>
                        {getAvailableVariables().map((variable) => (
                          <MenuItem key={variable._id} value={variable.key}>
                            <Box>
                              <Typography
                                sx={{ fontWeight: 600, fontSize: "14px" }}
                              >
                                {variable.key}
                              </Typography>
                              <Typography
                                sx={{ fontSize: "12px", color: "#666" }}
                              >
                                {variable.label}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </CustomSelectBox>

                      {/* List of selected variables using DraggableList */}
                      {getSelectedCount() > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <DraggableList
                            items={getSelectedVariablesForDraggable()}
                            onReorder={handleVariableReorder}
                            onRemove={handleDraggableRemove}
                            emptyMessage="No variables selected"
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Grid>

          {/* Right Panel - Template Preview */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            sx={{ 
              p: 3,
              overflowY: "auto",
              // backgroundColor: "#fff"
            }}
          >
            <Box>
              {!selectedTemplate ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "400px",
                    // backgroundColor: "#fff",
                    borderRadius: "12px",
                    border: "2px dashed #ccc",
                  }}
                >
                  <Typography sx={{ color: "#999" }}>
                    Select a template to see preview
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    // backgroundColor: "#e5ddd5",
                    borderRadius: "12px",
                    p: 2,
                    maxWidth: "400px",
                    margin: "0 auto",
                  }}
                >
                  {/* WhatsApp Message Bubble */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      padding: "12px",
                      // boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      border: "1px solid #f3f3f3",
                      position: "relative",
                    }}
                  >
                    {/* Image Preview for IMAGE type */}
                    {selectedTemplate.type === "IMAGE" && (
                      <Box sx={{ mb: 2, position: "relative" }}>
                        {imageUrl ? (
                          <>
                            <Box
                              component="img"
                              src={imageUrl}
                              alt="Template preview"
                              sx={{
                                width: "100%",
                                height: "200px",
                                objectFit: "cover",
                                borderRadius: "8px",
                                display: "block",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <Box
                              sx={{
                                width: "100%",
                                height: "200px",
                                backgroundColor: "#f0f0f0",
                                borderRadius: "8px",
                                display: "none",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "2px dashed #ccc",
                              }}
                            >
                              <Typography sx={{ color: "#999" }}>
                                Invalid image URL
                              </Typography>
                            </Box>
                            
                            {/* Action Buttons */}
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                zIndex: 2,
                              }}
                            >
                              <TableActionButtons 
                                showEdit 
                                showDelete
                                handleEdit={handleEditImage}
                                handleDelete={handleDeleteImage}
                              />
                            </Box>
                            
                            {/* Hidden file input */}
                            <input
                              ref={imageInputRef}
                              hidden
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                            />
                          </>
                        ) : (
                          <LoadingButton
                            component="label"
                            variant="outlined"
                            color="secondary"
                            loading={uploadingImage}
                            startIcon={<CloudUploadIcon />}
                            sx={{
                              width: "100%",
                              height: "200px",
                              borderRadius: "8px",
                              borderStyle: "dashed",
                              borderWidth: "2px",
                              textTransform: "capitalize",
                              fontSize: "16px",
                              fontWeight: 500,
                              backgroundColor: "#fafafa",
                              "&:hover": {
                                backgroundColor: "#f0f0f0",
                              },
                            }}
                          >
                            Upload Image (Max 0.5MB)
                            <input
                              hidden
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                            />
                          </LoadingButton>
                        )}
                        <Chip
                          label="IMAGE"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0,0,0,0.7)",
                            color: "#fff",
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        />
                      </Box>
                    )}

                    {/* Template Content */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#000",
                        lineHeight: 1.5,
                      }}
                    >
                      {getTemplateText() || "No template content available"}
                    </Typography>

                    {/* Template Info Footer */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #eee" }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: "11px", color: "#666" }}>
                            Type: <strong>{selectedTemplate.type}</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: "11px", color: "#666" }}>
                            Parameters: <strong>{selectedTemplate.num_of_parameters || 0}</strong>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Variable Mapping Info */}
                  {getSelectedCount() > 0 && (
                    <Box sx={{ mt: 2 ,
                      borderRadius: "10px",
                      padding: "12px",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      border: "1px solid #f3f3f3", }}>
                      <Typography 
                        sx={{ 
                          fontSize: "12px", 
                          fontWeight: 600, 
                          mb: 1,
                          // color: "#555"
                          
                        }}
                      >
                        Variable Mapping:
                      </Typography>
                      <Stack spacing={0.5}>
                        {templateVariables
                          .filter((placeholder) => variableNames[placeholder])
                          .sort(
                            (a, b) =>
                              variablePriorities[a] - variablePriorities[b]
                          )
                          .map((placeholder, index) => {
                            const variable = (variablesData || []).find(
                              (v) => v.key === variableNames[placeholder]
                            );
                            return (
                              <Box
                                key={placeholder}
                                sx={{
                                  backgroundColor: "#fff",
                                  padding: "8px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Chip
                                  label={`{{${index + 1}}}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#dcf8c6",
                                    fontSize: "11px",
                                    height: "20px",
                                  }}
                                />
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                  = {variable?.key || "Unknown"}
                                </Typography>
                              </Box>
                            );
                          })}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
         variant="outlined" 
         color="secondary" onClick={onClose} 
         size="large"
>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="secondary"
          onClick={handleSaveClick}
          loading={loading}
          disabled={!selectedTemplate}
          size="large"
        >
          Update
        </LoadingButton>
      </DialogActions>
    </CommonDialog>
  );
};

export default EditTemplateDialog;
